/**
 * The one row anatomy for every channel on an activity feed: a 16px icon in the
 * box's one gutter, a 2-line headline, a time pinned top-right, a second line (who /
 * count / outcome), a one-line preview, and one quiet "Belongs Here" when the
 * row (or every message of a grouped conversation) isn't on the record being
 * viewed. The whole card is the expand trigger — no separate "Expand" row.
 *
 * Built additive to the existing `ContactRow` (contact-history/ContactTimeline.tsx)
 * rather than replacing it — this shell is adopted by the deal timeline's
 * conversation cards first; other surfaces (the LCC contact sheet, Customer 360)
 * are candidates for a later migration, not this one.
 */

import { type ReactNode } from "react";
import { ChevronRight, Plus, type LucideIcon } from "lucide-react";

import { chevronClass } from "@/components/motion";
import {
  GUTTER,
  GUTTER_TILE,
  OBJ_PAD,
  ROW,
  SYSTEM_TILE,
  T_BODY,
  T_META,
  TILE_ICON,
} from "@/components/records/visual-system";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type ActivityCardKind, tileTone } from "./icon-accent";
import { NARROW_ONLY_LINE, RowMetaTime, RowTrailing } from "./row-trailing";

export interface BelongsHereAction {
  /** Moves everything not yet on this deal — or, when `failure` is set, retries only the messages
   *  that didn't land. The caller decides which contacts that is; this component just fires it. */
  onClick: () => void;
  /** The move (or retry) is in flight — the button shows a busy label and disables itself. */
  pending: boolean;
  /** Set after a batched move left some messages unmoved: renders "k of n couldn't move · Retry"
   *  in place of the quiet action. `onClick` must already be wired to retry only the failed ones. */
  failure?: { failedCount: number; totalCount: number } | null;
}

/**
 * Title Case, borderless, ~24px, green text with a light hover fill and a
 * small `+` — a quiet row action, not a bordered button. It repeats on every
 * row of the customer's other history, so a bordered button would be louder
 * than the content it sits beside.
 *
 * A partial failure replaces it in place with the count and a Retry — never a
 * toast, which the coordinator ruled out: a card already shown names its own
 * outcome, so the person reading it doesn't have to connect a toast back to
 * which of several cards it was about.
 */
export function BelongsHereButton({ onClick, pending, failure }: BelongsHereAction) {
  if (failure) {
    return (
      <span className="flex shrink-0 items-center gap-1.5 text-xs" data-testid="belongs-here-failure">
        <span className="text-destructive">
          {failure.failedCount} of {failure.totalCount} couldn’t move
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary"
          onClick={onClick}
          disabled={pending}
          aria-busy={pending}
        >
          {pending ? "Retrying…" : "Retry"}
        </Button>
      </span>
    );
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 shrink-0 gap-1 px-1.5 text-xs font-medium text-primary hover:bg-primary/10 hover:text-primary"
      onClick={onClick}
      disabled={pending}
      aria-busy={pending}
    >
      <Plus className="h-3 w-3" aria-hidden="true" />
      {pending ? "Moving…" : "Belongs Here"}
    </Button>
  );
}

export function ActivityCard({
  icon: Icon,
  kind,
  title,
  who,
  time,
  timeTitle,
  preview,
  underPreview,
  belongsHere,
  brandLabel,
  elsewhereLabel,
  trailing,
  actions,
  expandable = true,
  open,
  onOpenChange,
  children,
  footer,
  className,
}: {
  icon: LucideIcon;
  /** Which accent tile to draw (mock §3b "Icon tile" — one accent per item kind,
   *  reusing the app's existing identity-accent system). */
  kind: ActivityCardKind;
  /** Headline: the most specific human content. Wraps to 2 lines rather than
   *  clipping — content, not a label, so there's no ellipsis to hide behind. */
  title: ReactNode;
  /** Second line: who / count / outcome. */
  who?: ReactNode;
  time?: ReactNode;
  timeTitle?: string;
  /** A small trailing mark before the time — today only `SourceLink` ("ST ↗"),
   *  for the one card-shaped exception (a scheduled visit) that still points
   *  at a ServiceTitan record. */
  trailing?: ReactNode;
  /** Row controls (a note's delete) in the header beside the time: revealed on
   *  hover/focus with a fine pointer, always shown on touch. */
  actions?: ReactNode;
  /** What was said — shown ONLY when there's something real to show (never an
   *  invented snippet). Clamped to 2 lines while closed, in full once opened, so
   *  the opened body never has to say it again. Omitted channels (e.g. a thin
   *  system-event line) don't use this shell at all — see `SystemLine`. */
  preview?: ReactNode;
  /** Under the preview, outside its 2-line clamp: an email's files. */
  underPreview?: ReactNode;
  belongsHere?: BelongsHereAction | null;
  /** Names a sibling brand the row was logged under, same rule `ContactRow` uses
   *  (single-brand surfaces omit it). Carries the same `contact-brand` test id so
   *  existing assertions hold across both rows. */
  brandLabel?: string | null;
  /** "On another deal · <name>" / "Not on any open deal" — the quiet line version
   *  of `ContactRow`'s dashed-border banner (no second button here; "Belongs Here"
   *  above already covers the action). Same `contact-deal-label` test id. */
  elsewhereLabel?: ReactNode | null;
  /** False for a row with nothing to open — a one-line no-answer/missed call. */
  expandable?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The opened body: only what the header and preview don't already say. */
  children?: ReactNode;
  /** Under the opened card, the card's full width rather than the content column beside the
   *  icon tile: an email's Reply and its composer, which need the room. */
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "group/activity",
        OBJ_PAD,
        expandable && "cursor-pointer",
        className,
      )}
      data-testid="activity-row"
      data-kind={kind}
      // The card itself is the expand trigger (mock §3 "Expand") — a nested
      // interactive element (Belongs Here, a link) stops its own click below.
      onClick={expandable ? () => onOpenChange(!open) : undefined}
      role={expandable ? "button" : undefined}
      tabIndex={expandable ? 0 : undefined}
      onKeyDown={
        expandable
          ? (e) => {
              // Only the card's own Enter/Space toggles it — a nested control (an
              // older-email row button, a link) handles its own key and must not
              // also close the card out from under it.
              if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                e.preventDefault();
                onOpenChange(!open);
              }
            }
          : undefined
      }
      aria-expanded={expandable ? open : undefined}
    >
      <div className={ROW}>
        {/* The entry's icon on its kind's tinted tile (`CARD_ACCENT`), in the surface's one gutter. */}
        <span className={GUTTER}>
          <span className={cn(GUTTER_TILE, tileTone(kind))} data-testid="activity-tile">
            <Icon className={TILE_ICON} aria-hidden="true" />
          </span>
        </span>
        <div className="min-w-0" data-testid="activity-row-content">
          <div className="flex items-start gap-1">
            {/* Body, 2 lines while closed, in full once opened. */}
            <span
              className={cn(T_BODY, "min-w-0", open ? "whitespace-pre-line break-words" : "line-clamp-2")}
              data-testid="activity-row-title"
            >
              {title}
            </span>
            {expandable ? (
              <span className="flex h-5 shrink-0 items-center text-muted-foreground" aria-hidden="true">
                <ChevronRight className={cn("size-[16px]", chevronClass(open))} />
              </span>
            ) : null}
            {actions ? (
              <span
                className="ml-auto flex shrink-0 items-center gap-1 pointer-fine:opacity-0 pointer-fine:group-hover/activity:opacity-100 pointer-fine:group-focus-within/activity:opacity-100"
                data-testid="activity-row-actions"
                onClick={(e) => e.stopPropagation()}
              >
                {actions}
              </span>
            ) : null}
          </div>
          {/* In a narrow list the time joins this line; a row with no other meta draws the line only there. */}
          {who || brandLabel || time ? (
            <div
              className={cn("mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5", T_META, !(who || brandLabel) && NARROW_ONLY_LINE)}
            >
              {who}
              {brandLabel ? (
                <span
                  className="rounded bg-muted px-1.5 py-0.5"
                  data-testid="contact-brand"
                  title="Logged under a sibling brand of this customer"
                >
                  {brandLabel}
                </span>
              ) : null}
              <RowMetaTime time={time} timeTitle={timeTitle} />
            </div>
          ) : null}
          {elsewhereLabel ? (
            <p className={T_META} data-testid="contact-deal-label">
              {elsewhereLabel}
            </p>
          ) : null}
          {preview ? (
            <p
              className={cn("mt-0.5", T_META, open ? "whitespace-pre-line break-words" : "line-clamp-2")}
              data-testid="activity-row-preview"
            >
              {preview}
            </p>
          ) : null}
          {underPreview}
          {(belongsHere || trailing) ? (
            <div className="mt-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {belongsHere ? <BelongsHereButton {...belongsHere} /> : null}
              {trailing}
            </div>
          ) : null}
          {expandable && open && children ? (
            <div className="mt-2 border-t border-border/60 pt-2" onClick={(e) => e.stopPropagation()}>
              {children}
              {/* A long open email folds from its foot too, not only from the chevron at its top. */}
              <button
                type="button"
                className={cn("mt-2 inline-flex h-7 items-center gap-1 rounded px-1.5 hover:bg-muted", T_META)}
                onClick={() => onOpenChange(false)}
                data-testid="activity-row-collapse"
              >
                <ChevronRight className={cn("size-[16px]", chevronClass(true))} aria-hidden="true" />
                Collapse
              </button>
            </div>
          ) : null}
        </div>
        {/* The right edge: the time, in the same cells every timeline row uses, so all times line up. */}
        <RowTrailing lineClassName="h-5" time={time} timeTitle={timeTitle} />
      </div>
      {expandable && open && footer ? (
        <div className="min-w-0" data-testid="activity-row-footer" onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      ) : null}
    </div>
  );
}

/** A one-line fact with nothing to open — an unrecorded no-answer or missed
 *  call (mock §3 "a missed call has no expand"). The same box, padding and
 *  icon gutter as every other timeline entry; only its height differs.
 *
 *  Still carries the same move/brand/elsewhere affordances a full `ActivityCard`
 *  does — a one-line fact can still be on the wrong deal and need moving. */
export function OneLineRow({
  icon: Icon,
  label,
  time,
  belongsHere,
  brandLabel,
  elsewhereLabel,
}: {
  icon: LucideIcon;
  label: ReactNode;
  time?: ReactNode;
  /** Present only when the row isn't on the deal being viewed — same contract as `ActivityCard`. */
  belongsHere?: BelongsHereAction | null;
  brandLabel?: string | null;
  elsewhereLabel?: ReactNode;
}) {
  return (
    <div
      className={cn(ROW, OBJ_PAD)}
      data-testid="activity-row-oneline"
    >
      <span className={GUTTER}>
        <span className={cn(GUTTER_TILE, SYSTEM_TILE)}>
          <Icon className={TILE_ICON} aria-hidden="true" />
        </span>
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span className={cn(T_BODY, "min-w-0 truncate")}>{label}</span>
        {brandLabel ? (
          <span
            className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs"
            data-testid="contact-brand"
            title="Logged under a sibling brand of this customer"
          >
            {brandLabel}
          </span>
        ) : null}
        <RowMetaTime time={time} className="shrink-0" />
        {elsewhereLabel ? (
          <span className={cn("shrink-0", T_META)} data-testid="contact-deal-label">
            {elsewhereLabel}
          </span>
        ) : null}
        {belongsHere ? (
          <span onClick={(e) => e.stopPropagation()}>
            <BelongsHereButton {...belongsHere} />
          </span>
        ) : null}
      </span>
      <RowTrailing lineClassName="h-5" time={time} />
    </div>
  );
}
